import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { Body } from '../../../../styled components/Typography';
import {
  changedTint,
  deletedText,
  happeningNowDotRoom,
  pastRow,
  setMemberRule,
  statusBackground
} from '../../ListViewBookings/ListViewBookings.style';
import type { RowDisplayStatus } from '../../../listViewRowStatus';

/* The row of the table one booking is on, and its cells. The cells are also
 * what ListViewTableLoadingRows draws its skeletons in, so the rows that are
 * still loading have the columns and the spacing of the ones that arrive. */

export const BookingRow = styled.tr<{
  $status: RowDisplayStatus;
  /* The row of a booking that is a member of a set: the members of a set are
     shaded as one block under the row of the set they belong to */
  $isSetMember: boolean;
}>(({ $status, $isSetMember }) => [
  $isSetMember && tw`bg-silver-lighten-30`,
  ...statusBackground($status, $isSetMember),
  $status === 'past' && pastRow
]);

/* Values wrap rather than widen the table: the table takes the width of the
   box it is in, and only a column's minimum (see TableHeaderCell) puts it past
   that and gives TableScrollBox something to scroll */
export const BookingCell = styled.td<{ $changed: boolean }>(({ $changed }) => [
  tw`align-top break-words px-8 py-4 border-0 border-b border-solid border-silver-lighten-20`,
  $changed && changedTint
]);

/* The cell that carries the nesting: the resource name, with the chevron and
   the member count of a set, and for a member the rule that ties it to its
   set. Its own left padding is the indent, so the rule sits at the level the
   row is nested at. */
export const NestingCell = styled(BookingCell)<{ $depth: number }>(({ $depth }) => [
  /* The name on the row is kept on one line and cut off with an ellipsis
     where it does not fit (see CellText in ListViewTableRow), and a line that
     is never broken asks the table for the room its longest name needs, and a
     single long name would then push every other column out of the view and
     leave the reader scrolling for them. Asking for no width at all instead
     leaves the column the room the other columns do not need, and the
     ellipsis takes care of the rest. Its floor is the minimum in
     minimumColumnWidthInCharacters. */
  css`
    max-width: 0;
  `,
  $depth > 0 && setMemberRule,
  /* A set can sit inside a set without limit, so the indent isn't a fixed set
     of Tailwind classes; it is the 16px of the spacing scale, multiplied, on
     top of the cell's own 8px of padding. */
  $depth > 0 &&
    css`
      padding-left: calc(0.5rem + ${$depth}rem); /* 16px per level */
    `
]);

/* The chevron and the name of a set, both on one line: a row that never
   wraps, so the two stay together whatever the column's width. The name is
   the one of them that gives way - it is truncated (see CellText in
   ListViewTableRow), and the chevron keeps its size beside it. */
export const NestingCellContent = styled.div(tw`flex items-center flex-nowrap`);

/* That room, held open on the rows that are not running, so the values of the
   column all start at the same place whether their booking carries a dot or
   not */
export const HappeningNowDotPlaceholder = styled.div(() => [
  tw`shrink-0`,
  css`
    width: ${happeningNowDotRoom};
  `
]);

/* The dot and the value it is put in front of, on one line: a row, so the
   value can never end up under the dot however narrow its column gets - it
   wraps within its own place beside it, the way the name does beside the
   chevron in NestingCellContent. The times the dot marks are short enough
   that they do not wrap at all. */
export const HappeningNowCellContent = styled.div(tw`flex items-center`);

export const CellText = styled(Body)<{ $deleted: boolean }>(({ $deleted }) => [
  $deleted && deletedText
]);
